import { ApiResult } from "src/api/types";
import { usePageConfigProviderContext } from "src/features/PageConfigProvider";
import { useTranslation } from "src/hooks";
import { selectCrossMerchant } from "src/reducer/crossMerchantSlice";
import { useAppSelector } from "src/reducer/hooks";
import { enumOperationBatchStatus } from "src/utils/constant";
import {
  findChainInfo,
  listMappingTransform,
  toDisplayTime,
} from "src/utils/helper";
import { GetListRes } from "../types/GetListRes";
import { TableColumnKey, TableRow } from "../types/Table";

interface UseTableRowsProps {
  apiList: ApiResult<GetListRes>;
}

type MapResultToTupleArray = (
  row: GetListRes[],
  omitKeys: string[],
) => any[][][];

const useTableRows = (props: UseTableRowsProps) => {
  const { translatePrefix } = usePageConfigProviderContext();
  const { merchantMapping } = useAppSelector(selectCrossMerchant);
  const { t, te, translate } = useTranslation(translatePrefix);
  const { apiList } = props;

  const getMerchantName = (merchantId: string | undefined) => {
    if (!merchantId) return "";
    return merchantMapping?.[Number(merchantId)] ?? "";
  };

  const mapResultToTupleArray: MapResultToTupleArray = (rows, omitKeys) => {
    return rows.map((item) => {
      const mappedResult = [
        [TableColumnKey.BatchId, t(TableColumnKey.BatchId), item?.batch_id],
        [
          TableColumnKey.MerchantName,
          t(TableColumnKey.MerchantName),
          getMerchantName(item.merchant_id),
        ],
        [
          TableColumnKey.ChainName,
          t(TableColumnKey.ChainName),
          findChainInfo(item.chain_type, item.chain_id)?.name,
        ],
        [TableColumnKey.Currency, t(TableColumnKey.Currency), item.asset_name],
        [
          TableColumnKey.SmartAccountType,
          t(TableColumnKey.SmartAccountType),
          item.batch.smart_account_type ? te("yes") : te("no"),
          ,
        ],
        [
          TableColumnKey.SpenderAddress,
          t(TableColumnKey.SpenderAddress),
          item.spender_address,
        ],
        [
          TableColumnKey.InvokerAddress,
          t(TableColumnKey.InvokerAddress),
          item.invoker_address,
        ],
        [
          TableColumnKey.SpenderContractVersion,
          t(TableColumnKey.SpenderContractVersion),
          item.spender_contract_version,
        ],
        [
          TableColumnKey.SpenderContractId,
          t(TableColumnKey.SpenderContractId),
          item.spender_contract_id,
        ],
        [
          TableColumnKey.Status,
          t(TableColumnKey.Status),
          translate(
            `enumConstants.${enumOperationBatchStatus[item.batch.status]}`,
          ),
          ,
        ],
        [
          TableColumnKey.TotalCount,
          t(TableColumnKey.TotalCount),
          item.total_count,
        ],
        [
          TableColumnKey.ClientData,
          t(TableColumnKey.ClientData),
          item.client_data,
        ],
        [
          TableColumnKey.CreationTime,
          t(TableColumnKey.CreationTime),
          toDisplayTime(item.created_date),
        ],
        [
          TableColumnKey.LastModifiedTime,
          t(TableColumnKey.LastModifiedTime),
          toDisplayTime(item.last_modified_date),
        ],
        ["rawData", "", item],
      ].filter(([key]) => !omitKeys.includes(key as string));
      return mappedResult;
    });
  };

  const res = mapResultToTupleArray(apiList.rows, []);
  const rows: TableRow[] = res.map(listMappingTransform("key"));

  return {
    rows,
    totalCounts: apiList.count,
    mapResultToTupleArray,
  };
};

export default useTableRows;
